import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faTimes } from "@fortawesome/free-solid-svg-icons";
import logo from "../assets/logo.png";

const HeaderContainer = styled.header`
  background-color: rgba(255, 255, 255, 0.95);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  position: fixed;
  width: 100%;
  z-index: 1000;
  transition: all 0.3s ease;
`;

const HeaderContent = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  height: 120px;

  @media (max-width: 576px) {
    height: 100px;
  }
`;

const Logo = styled.img`
  height: 150px;

  @media (max-width: 576px) {
    height: 100px;
  }
`;

const DesktopNav = styled.nav`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const DesktopMenu = styled.ul`
  display: flex;
  list-style: none;
`;

const MobileNav = styled.nav`
  position: fixed;
  top: ${(props) => props.height || "120px"};
  left: ${(props) => (props.open ? "0" : "-100%")};
  width: 100%;
  height: calc(100vh - ${(props) => props.height || "120px"});
  background-color: white;
  transition: left 0.4s ease;
  box-shadow: ${(props) =>
    props.open ? "0 10px 27px rgba(0, 0, 0, 0.05)" : "none"};
  z-index: 999;

  @media (min-width: 769px) {
    display: none;
  }

  @media (max-width: 576px) {
    top: 100px;
    height: calc(100vh - 100px);
  }
`;

const MobileMenu = styled.ul`
  list-style: none;
  padding: 30px 0;
  margin: 0;
  text-align: center;
`;

const NavItem = styled.li`
  margin: ${(props) => (props.mobile ? "20px 0" : "0 0 0 30px")};
`;

const NavLink = styled(Link)`
  color: #333;
  text-decoration: none;
  font-weight: 500;
  position: relative;
  transition: all 0.3s ease;
  font-size: ${(props) => (props.mobile ? "1.2rem" : "1rem")};

  &:hover {
    color: #1a5f7a;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background-color: #1a5f7a;
    transition: all 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }

  &.active {
    color: #1a5f7a;

    &::after {
      width: 100%;
    }
  }
`;

const MenuToggle = styled.button`
  display: none;
  background: transparent;
  border: none;
  font-size: 28px;
  color: #333;
  cursor: pointer;
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  padding: 10px;
  z-index: 1001;
  outline: none;

  @media (max-width: 768px) {
    display: block;
  }
`;

const Overlay = styled.div`
  display: ${(props) => (props.open ? "block" : "none")};
  position: fixed;
  top: ${(props) => props.top || "120px"};
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 998;

  @media (min-width: 769px) {
    display: none;
  }

  @media (max-width: 576px) {
    top: 100px;
  }
`;

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Handler for controlling scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Close menu on route change
  useEffect(() => {
    // Only close the menu when the location changes, not when menuOpen changes
    setMenuOpen(false);

    // Control body scroll is still fine to have here, since we want this to run when menuOpen changes
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [location]); // Only depend on location, not menuOpen

  // Add a separate effect for body scroll control
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [menuOpen]); // This effect only runs when menuOpen changes

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/apartments", label: "Apartments" },
    { path: "/contact", label: "Contact" },
    { path: "/join", label: "Join" },
  ];

  return (
    <HeaderContainer
      style={{
        boxShadow: scrolled ? "0 2px 10px rgba(0, 0, 0, 0.1)" : "none",
      }}
    >
      <HeaderContent>
        <Link to="/">
          <Logo src={logo} alt="Skyline Terrace Logo" />
        </Link>

        {/* Desktop Navigation */}
        <DesktopNav>
          <DesktopMenu>
            {navLinks.map((link) => (
              <NavItem key={link.path}>
                <NavLink
                  to={link.path}
                  className={isActive(link.path) ? "active" : ""}
                >
                  {link.label}
                </NavLink>
              </NavItem>
            ))}
          </DesktopMenu>
        </DesktopNav>

        {/* Mobile Menu Toggle Button */}
        <MenuToggle
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle mobile menu"
        >
          <FontAwesomeIcon icon={menuOpen ? faTimes : faBars} />
        </MenuToggle>
      </HeaderContent>

      {/* Mobile Navigation */}
      <MobileNav open={menuOpen}>
        <MobileMenu>
          {navLinks.map((link) => (
            <NavItem key={link.path} mobile>
              <NavLink
                to={link.path}
                mobile
                className={isActive(link.path) ? "active" : ""}
                onClick={() => setMenuOpen(false)}
              >
                {link.label}
              </NavLink>
            </NavItem>
          ))}
        </MobileMenu>
      </MobileNav>

      {/* Overlay when mobile menu is open */}
      <Overlay open={menuOpen} onClick={() => setMenuOpen(false)} />
    </HeaderContainer>
  );
};

export default Header;
